import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useMutation } from "@apollo/client";
import { Card, Avatar, Typography, Switch, Input, Row, Col, Tag, Empty, Spin, message, Collapse } from "antd";
import { SearchOutlined, MailOutlined, UserOutlined, TeamOutlined } from "@ant-design/icons";
import { UPDATE_PERSONAL_OUTLOOK_ACCESS } from "../../../Queries/SalesEmails";

const { Title, Text } = Typography;
const { Panel } = Collapse;

const filterActive = (emps) =>
  emps?.filter((e) => e.employeestatus === "Active" && e.department?.trim()) || [];

export default function PersonalOutlookAccess() {
  const allEmployees = useSelector((s) => s.employee.AllEmployees);
  const employees = useMemo(() => filterActive(allEmployees), [allEmployees]);

  const [searchTerm, setSearchTerm] = useState("");
  const [pendingMap, setPendingMap] = useState({});   // id -> true/false (unsaved)
  const [savingId, setSavingId] = useState(null);

  const [updateAccess] = useMutation(UPDATE_PERSONAL_OUTLOOK_ACCESS);

  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return q
      ? employees.filter(
          (e) =>
            e.firstname?.toLowerCase().includes(q) ||
            e.lastname?.toLowerCase().includes(q) ||
            e.department?.toLowerCase().includes(q)
        )
      : employees;
  }, [employees, searchTerm]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((e) => {
      if (!g[e.department]) g[e.department] = [];
      g[e.department].push(e);
    });
    return g;
  }, [filtered]);

  const getEnabled = (emp) =>
    pendingMap[emp.id] !== undefined ? pendingMap[emp.id] : !!emp.personalOutlookEnabled;

  const handleToggle = async (emp, val) => {
    setPendingMap((prev) => ({ ...prev, [emp.id]: val }));
    setSavingId(emp.id);
    try {
      const { data } = await updateAccess({
        variables: { employeeId: emp.id, enabled: val },
      });
      if (data?.updatePersonalOutlookAccess?.success) {
        message.success(`Personal Outlook ${val ? "enabled" : "disabled"} for ${emp.firstname} ${emp.lastname}`);
      } else {
        message.error(data?.updatePersonalOutlookAccess?.message || "Failed to update");
        setPendingMap((prev) => ({ ...prev, [emp.id]: !val }));
      }
    } catch {
      message.error("Network error");
      setPendingMap((prev) => ({ ...prev, [emp.id]: !val }));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div style={{ padding: "24px", background: "#f5f6fa", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 10,
            background: "linear-gradient(135deg, #667eea, #764ba2)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <MailOutlined style={{ color: "#fff", fontSize: 20 }} />
          </div>
          <div>
            <Title level={4} style={{ margin: 0 }}>Personal Outlook Access</Title>
            <Text type="secondary">Control which employees can access their personal Outlook inbox</Text>
          </div>
        </div>
      </div>

      {/* Stats row */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12, background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Total Employees</Text>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#1a1a2e" }}>{employees.length}</div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12, background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Access Enabled</Text>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#52c41a" }}>
              {employees.filter((e) => getEnabled(e)).length}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12, background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Access Disabled</Text>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#ff4d4f" }}>
              {employees.filter((e) => !getEnabled(e)).length}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12, background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Departments</Text>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#1677ff" }}>{Object.keys(grouped).length}</div>
          </Card>
        </Col>
      </Row>

      {/* Search */}
      <Input
        prefix={<SearchOutlined style={{ color: "#bbb" }} />}
        placeholder="Search by name or department..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ marginBottom: 20, borderRadius: 10, maxWidth: 400 }}
        allowClear
      />

      {/* Employee list grouped by department */}
      {Object.keys(grouped).length === 0 ? (
        <Empty description="No employees found" />
      ) : (
        <Collapse
          defaultActiveKey={Object.keys(grouped)}
          bordered={false}
          style={{ background: "transparent" }}
        >
          {Object.entries(grouped).map(([dept, emps]) => (
            <Panel
              key={dept}
              header={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <TeamOutlined style={{ color: "#667eea" }} />
                  <Text strong>{dept}</Text>
                  <Tag color="blue" style={{ marginLeft: 4 }}>{emps.length}</Tag>
                  <Tag color="green" style={{ marginLeft: 2 }}>
                    {emps.filter((e) => getEnabled(e)).length} enabled
                  </Tag>
                </div>
              }
              style={{ marginBottom: 12, background: "#fff", borderRadius: 12, border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
            >
              <Row gutter={[12, 12]}>
                {emps.map((emp) => {
                  const enabled = getEnabled(emp);
                  const isSaving = savingId === emp.id;
                  return (
                    <Col xs={24} sm={12} md={8} lg={6} key={emp.id}>
                      <Card
                        bordered={false}
                        style={{
                          borderRadius: 10,
                          background: enabled ? "#f6ffed" : "#fff7f7",
                          border: `1.5px solid ${enabled ? "#b7eb8f" : "#ffa39e"}`,
                          transition: "all 0.2s",
                        }}
                        bodyStyle={{ padding: "14px 16px" }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <Avatar
                            size={36}
                            style={{
                              background: enabled
                                ? "linear-gradient(135deg, #52c41a, #389e0d)"
                                : "linear-gradient(135deg, #d9d9d9, #bfbfbf)",
                              flexShrink: 0,
                            }}
                          >
                            {emp.firstname?.[0]}{emp.lastname?.[0]}
                          </Avatar>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Text strong style={{ display: "block", fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {emp.firstname} {emp.lastname}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 11 }}>{emp.email || "—"}</Text>
                          </div>
                          {isSaving ? (
                            <Spin size="small" />
                          ) : (
                            <Switch
                              checked={enabled}
                              onChange={(val) => handleToggle(emp, val)}
                              size="small"
                              checkedChildren="On"
                              unCheckedChildren="Off"
                            />
                          )}
                        </div>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </Panel>
          ))}
        </Collapse>
      )}
    </div>
  );
}
